import type { Descendant } from 'slate';

import type { TextAnnotationMap } from 'src/data/annotation';
import type { TargetAudienceInsights } from 'src/data/audiencesTargeting';
import type { BenchmarkConfigSingle } from 'src/data/benchmark';
import {
  type GenerationTool,
  generationToolAllowsScores,
  isImageOnlyGenerationTool,
} from 'src/data/generationTool';
import type { BackendSharedDocumentVariationDetailsView } from 'src/types/backend';

export const getInstanceIdFromDocumentDomElement = (element: HTMLElement) =>
  element?.dataset?.instanceId;

export const searchForVariationElement = (domElement: HTMLElement | null): HTMLElement | null => {
  if (domElement == null) {
    return null;
  }
  if (domElement.dataset?.variation != null) {
    return domElement;
  }

  return searchForVariationElement(domElement.parentElement);
};

// A saved item of a document, as `collectDDVariationFromEditor` reads it from the editor.
export interface DocumentVariation {
  id: string;
  instanceId: string;
  assetGroupId?: string | null;
  assetType: string;
  generationTool: GenerationTool;
  mixtureSubAssetType?: string | null;
  score: number;
  text: string;
  editorValue: Descendant[];
  isScoreLocked?: boolean;
  performancePredictionsScoreLocked?: boolean;
  scoringMethod?: { name?: string } | null;
  secondaryScoringMethodName?: string | null;
  campaignKeywords?: string[] | null;
  benchmarkConfig?: BenchmarkConfigSingle;
}

// An item without a tool is scored. An image-only tool or a tool without scores shows no score.
export const variationHasDocumentScore = ({
  generationTool,
}: Pick<DocumentVariation, 'generationTool'>): boolean =>
  !generationTool ||
  (!isImageOnlyGenerationTool(generationTool) && generationToolAllowsScores(generationTool));

// The read-only insights of one saved item: the public shared page and a reviewer read the same.
export interface DocumentVariationDetails extends Pick<
  BackendSharedDocumentVariationDetailsView,
  'scoring' | 'validations'
> {
  annotations?: TextAnnotationMap | null;
  targetAudienceInsights: TargetAudienceInsights[];
}
